import { useTranslation } from 'react-i18next';
import { SelectOption } from '@anytag/shared-types';
import { useAllRegionsQuery } from '@anytag/graphql';
import { getSelectOptions } from '@anytag/shared-utils';
import { SelectorField, SingleSelectorFieldProps, MultipleSelectorFieldProps } from './selector-field';

interface BaseRegionFieldProps {
  countryId: string;
  provinceId?: string;
  skipQuery?: boolean;
}

export type SingleRegionFieldProps<T extends string> = SingleSelectorFieldProps<T> & BaseRegionFieldProps;
export type MultipleRegionFieldProps<T extends string> = MultipleSelectorFieldProps<T> & BaseRegionFieldProps;
export type RegionFieldProps<T extends string> = SingleRegionFieldProps<T> | MultipleRegionFieldProps<T>;

// Function overloads for proper type inference based on `multiple` prop
export function RegionField<T extends string>(props: MultipleRegionFieldProps<T>): React.ReactElement;
export function RegionField<T extends string>(props: SingleRegionFieldProps<T>): React.ReactElement;
export function RegionField<T extends string>({
  title,
  disabled,
  skipQuery,
  countryId,
  provinceId,
  ...restProps
}: RegionFieldProps<T>) {
  const { t } = useTranslation();
  const { data, error } = useAllRegionsQuery({
    variables: { countryId, provinceId },
    skip: skipQuery || !countryId,
    fetchPolicy: 'cache-and-network'
  });
  const options = data ? getSelectOptions(data.allRegions) : [];
  const translatedOptions = options.map<SelectOption<string>>((item) => ({
    ...item,
    label: t(item.label || '') || ''
  }));

  return (
    <SelectorField<T>
      {...(restProps as SingleSelectorFieldProps<T>)}
      options={translatedOptions}
      title={title ?? t('Selector.Region')}
      disabled={disabled || !!error || !countryId || options.length === 0}
    />
  );
}
